import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { BossSoundState, QuizBossState, QuizState } from '~/bootcamp/components/Quiz/QuizData.models'
import { RootState } from '~/redux/store'
import { BossBehaviors } from '~/bootcamp/Activities/types/activities.types'
import { BossBattleEnemy } from '~/bootcamp/components/Quiz/QuizProps.models'

const initialBossState: QuizBossState = {
  hasStartedQuiz: false,
  bossCurrentBehavior: null,
  bossIsBusy: false,
  bossDamageAmount: 0,
  bossResponse: {
    state: 'ready',
    anim: null,
  },
  bossSound: BossSoundState.UNMUTE,
}

const initialBossBattleEnemy: BossBattleEnemy = {
  avatarGLBFilePath: '',
  avatarImagePath: {
    alt: '',
    srcKey: '',
  },
  dragonId: 0,
}

const initialState: QuizState = {
  bootcampKey: null,
  subtopic: null,
  startTime: null,
  totalQuestions: 0,
  completedQuestions: 0,
  wrongAnswer: 0,
  bossHealth: 100,
  damagePlayer: false,
  maxWrongAnswer: 0,
  questions: [],
  mathEnergyEarned: 0,
  badgeImage: {
    alt: 'Special Trophy',
    srcKey: 'bootcamp/bootcamp_module_list_trophy_logo.png',
  },
  bossBattleEnemy: initialBossBattleEnemy,
  ...initialBossState,
}

const quizSlice = createSlice({
  name: 'bootcamp-quiz',
  initialState,
  reducers: {
    setQuizState: (state: QuizState, action: PayloadAction<QuizState>) => {
      const { payload } = action

      if (payload) {
        const newStates = JSON.parse(JSON.stringify(payload))
        const damagePercentage = newStates.totalQuestions
          ? (1 / (newStates.totalQuestions - newStates.wrongAnswer)) * 100
          : 0

        return { ...state, ...newStates, bossDamageAmount: damagePercentage }
      } else {
        return { ...state }
      }
    },
    setStartedQuiz: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        hasStartedQuiz: action.payload,
      }
    },
    setMathEnergyEarned: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        mathEnergyEarned: action.payload,
      }
    },
    setBossCurrentBehavior: (state: QuizState, action: PayloadAction<BossBehaviors | null>) => {
      return {
        ...state,
        bossCurrentBehavior: action.payload,
        hasStartedQuiz: action.payload === BossBehaviors.RELOAD ? false : state.hasStartedQuiz,
      }
    },
    setBossIsBusy: (state: QuizState, action: PayloadAction<boolean>) => {
      return {
        ...state,
        bossIsBusy: action.payload,
      }
    },
    setBossResponse: (state: QuizState, action: PayloadAction<{ state: 'ready'; anim?: string }>) => {
      return {
        ...state,
        bossResponse: action.payload,
        hasStartedQuiz: action.payload.anim === 'Intro' ? true : state.hasStartedQuiz,
        bossIsBusy: false,
      }
    },
    setToggleBossSound: (state: QuizState) => {
      const isUnmute = state.bossSound === BossSoundState.UNMUTE

      return {
        ...state,
        bossSound: isUnmute ? BossSoundState.MUTE : BossSoundState.UNMUTE,
        bossCurrentBehavior: isUnmute ? BossBehaviors.MUTE : BossBehaviors.UNMUTE,
      }
    },
    damageBoss: (state: QuizState) => {
      let health = state.bossDamageAmount ? state.bossHealth - state.bossDamageAmount : 100
      if (state.totalQuestions === state.completedQuestions) {
        health = 0
      }
      return { ...state, bossHealth: health }
    },
    setDamagePlayer: (state: QuizState, action: PayloadAction<boolean>) => {
      return {
        ...state,
        damagePlayer: action.payload,
      }
    },
    resetQuizStore: (state: QuizState) => {
      return { ...state, ...initialState }
    },
    setBossBattleEnemy: (state: QuizState, action: PayloadAction<BossBattleEnemy>) => {
      return {
        ...state,
        bossBattleEnemy: action.payload,
      }
    },
  },
})

export const selectQuizState = (state: RootState): QuizState => {
  return state.bootcampQuiz
}

export const selectHasStartedQuiz = (state: RootState): QuizState['hasStartedQuiz'] => {
  return state.bootcampQuiz.hasStartedQuiz
}

export const selectBossCurrentBehavior = (state: RootState): QuizState['bossCurrentBehavior'] => {
  return state.bootcampQuiz.bossCurrentBehavior
}

export const selectBossIsBusy = (state: RootState): QuizState['bossIsBusy'] => {
  return state.bootcampQuiz.bossIsBusy
}

export const selectBossDamageAmount = (state: RootState): QuizState['bossDamageAmount'] => {
  return state.bootcampQuiz.bossDamageAmount
}

export const selectBossHealth = (state: RootState): QuizState['bossHealth'] => {
  return state.bootcampQuiz.bossHealth
}

export const selectDamagePlayer = (state: RootState): QuizState['damagePlayer'] => {
  return state.bootcampQuiz.damagePlayer
}

export const selectBossSound = (state: RootState): QuizState['bossSound'] => {
  return state.bootcampQuiz.bossSound
}

export const selectBossBattleEnemy = (state: RootState): BossBattleEnemy => {
  return state.bootcampQuiz.bossBattleEnemy
}

export const selectMathEnergyEarned = (state: RootState): QuizState['mathEnergyEarned'] => {
  return state.bootcampQuiz.mathEnergyEarned
}

export const selectCompletedQuestions = (state: RootState): QuizState['completedQuestions'] => {
  return state.bootcampQuiz.completedQuestions
}

export const selectWrongAnswer = (state: RootState): QuizState['wrongAnswer'] => {
  return state.bootcampQuiz.wrongAnswer
}

export const {
  setQuizState,
  setStartedQuiz,
  setBossCurrentBehavior,
  resetQuizStore,
  setBossIsBusy,
  setMathEnergyEarned,
  setBossResponse,
  setToggleBossSound,
  setBossBattleEnemy,
  damageBoss,
  setDamagePlayer,
} = quizSlice.actions
export default quizSlice.reducer
